import { useEffect, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import type { MaintenanceEvent } from '~/api/types'
import {
    submitMaintenanceChange,
    submitMaintenanceTurn,
} from './maintenance-actions'
import type { MaintenanceProgress, PendingClarification } from './types'
import { errMessage } from './utils'

type UseMaintenanceChangeSubmissionParams = {
    selectedId: string
    enabled: boolean
    activeEvent: MaintenanceEvent | null
    resetActiveSelection(): void
    addEventToHistory(event: MaintenanceEvent): void
    updateEventInHistory(event: MaintenanceEvent): void
}

/** 유지하기 변경 입력, 추가 확인, 제출 상태를 관리한다. */
export function useMaintenanceChangeSubmission({
    selectedId,
    enabled,
    activeEvent,
    resetActiveSelection,
    addEventToHistory,
    updateEventInHistory,
}: UseMaintenanceChangeSubmissionParams) {
    const [description, setDescription] = useState('')
    // 제출 즉시 사용자 말풍선으로 보여줄 입력(서버 응답 전 낙관적 에코). 응답이 오면 비운다.
    const [submittedText, setSubmittedText] = useState<string | null>(null)
    const [pendingClarification, setPendingClarification] =
        useState<PendingClarification | null>(null)
    const [progress, setProgress] = useState<MaintenanceProgress | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        if (!selectedId || !enabled) {
            setPendingClarification(null)
        }
    }, [selectedId, enabled])

    const focusTextarea = () => {
        textareaRef.current?.focus()
    }

    const clearSubmissionFeedback = () => {
        setPendingClarification(null)
        setActionError(null)
    }

    const submitMaintenance = async (overrideText?: string) => {
        // 클릭형 마지막 선택지처럼 합쳐진 답변을 직접 넘겨 제출하는 경우, 아직 반영되지
        // 않았을 수 있는 description state 대신 넘겨받은 텍스트를 우선 사용한다.
        const trimmed = (overrideText ?? description).trim()
        if (!trimmed || !selectedId || submitting) return
        setSubmitting(true)
        setActionError(null)
        setSubmittedText(trimmed)
        try {
            if (activeEvent && !pendingClarification) {
                const result = await submitMaintenanceTurn({
                    selectedId,
                    eventId: activeEvent.id,
                    message: trimmed,
                    onProgress: setProgress,
                })
                updateEventInHistory(result.event)
                setDescription('')
                window.setTimeout(focusTextarea, 0)
                return
            }

            resetActiveSelection()
            const result = await submitMaintenanceChange({
                selectedId,
                description: trimmed,
                pendingClarification,
                onProgress: setProgress,
            })
            if (result.type === 'clarification') {
                setPendingClarification(result.pendingClarification)
                setDescription('')
                window.setTimeout(focusTextarea, 0)
                return
            }

            addEventToHistory(result.event)
            setPendingClarification(null)
            setDescription('')
        } catch (err: unknown) {
            setActionError(errMessage(err, '변경 영향 분석에 실패했어요.'))
        } finally {
            setProgress(null)
            setSubmitting(false)
            setSubmittedText(null)
        }
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        void submitMaintenance()
    }

    // 클릭형 추가 확인의 마지막 질문에서 답을 고르면, 합쳐진 답변 전체를 그대로 넘겨
    // 곧바로 제출한다(description state 반영을 기다리지 않는다).
    const submitClarificationAnswers = (combined: string) => {
        setDescription(combined)
        void submitMaintenance(combined)
    }

    const handleDescriptionKeyDown = (
        e: KeyboardEvent<HTMLTextAreaElement>
    ) => {
        if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
        e.preventDefault()
        void submitMaintenance()
    }

    const focusNewChange = () => {
        setDescription('')
        resetActiveSelection()
        setPendingClarification(null)
        focusTextarea()
    }

    return {
        description,
        setDescription,
        submittedText,
        pendingClarification,
        progress,
        actionError,
        submitting,
        textareaRef,
        handleSubmit,
        handleDescriptionKeyDown,
        submitClarificationAnswers,
        focusNewChange,
        focusTextarea,
        clearSubmissionFeedback,
    }
}
